import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PaginationMeta } from '@optiflow/types';
import { useLocale } from '@/hooks/useLocale';
import { cn } from '@/utils/cn';

interface PaginationProps {
  pagination: PaginationMeta;
  onPageChange: (page: number) => void;
  isBusy?: boolean;
}

/**
 * Page controls for any list.
 *
 * The chevrons swap in Arabic: "previous" points toward the start of the reading direction, which
 * is the right in RTL. Everything else relies on logical properties.
 */
export function Pagination({ pagination, onPageChange, isBusy = false }: PaginationProps) {
  const { t } = useTranslation();
  const { isRtl } = useLocale();

  const { page, totalPages, totalItems, pageSize, hasNextPage, hasPreviousPage } = pagination;

  if (totalItems === 0) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, totalItems);

  const PreviousIcon = isRtl ? ChevronRight : ChevronLeft;
  const NextIcon = isRtl ? ChevronLeft : ChevronRight;

  const buttonClass =
    'border-line text-ink hover:bg-canvas inline-flex size-9 items-center justify-center rounded-lg border bg-white disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <nav
      aria-label={t('pagination.label')}
      className="border-line flex flex-wrap items-center justify-between gap-3 border-t px-5 py-3"
    >
      <p className="text-muted text-xs">
        {t('pagination.showing', { first, last, total: totalItems })}
      </p>

      <div className="flex items-center gap-2">
        <button
          type="button"
          className={buttonClass}
          onClick={() => onPageChange(page - 1)}
          disabled={!hasPreviousPage || isBusy}
          aria-label={t('pagination.previous')}
        >
          <PreviousIcon className="size-4" aria-hidden />
        </button>

        <span className={cn('text-muted px-1 text-xs', isBusy && 'opacity-50')}>
          {t('pagination.pageOf', { page, totalPages })}
        </span>

        <button
          type="button"
          className={buttonClass}
          onClick={() => onPageChange(page + 1)}
          disabled={!hasNextPage || isBusy}
          aria-label={t('pagination.next')}
        >
          <NextIcon className="size-4" aria-hidden />
        </button>
      </div>
    </nav>
  );
}
